/**
 * Workspace API service
 */

import { apiClient } from './api-client';
import type { Workspace, WorkspaceCreateRequest, WorkspaceUpdateRequest, PaginationOptions, PaginatedResponse, WorkspaceStatistics, WorkspaceFilterOptions } from '../types';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  timestamp: string;
}

interface WorkspaceListResponse {
  items: any[];
  nextToken?: string;
  hasMore: boolean;
}

// Map API response fields to frontend fields
function mapWorkspaceFromApi(apiWorkspace: any): Workspace {
  return {
    id: apiWorkspace.id ?? apiWorkspace.tenantId,
    tenantId: apiWorkspace.tenantId,
    accessEvoRegion: apiWorkspace.accessEvoRegion,
    applicationSubscriptionId: apiWorkspace.applicationSubscriptionId,
    organisationId: apiWorkspace.organisationId,
    salesforceId: apiWorkspace.salesforceId,
    insertTimestamp: new Date(apiWorkspace.insertTimestamp).toLocaleDateString() + ' ' + new Date(apiWorkspace.insertTimestamp).toLocaleTimeString(),
    updatedTimestamp: new Date(apiWorkspace.updatedTimestamp).toLocaleDateString() + ' ' + new Date(apiWorkspace.updatedTimestamp).toLocaleTimeString(),
  };
}

export class WorkspaceService {
  private readonly basePath = '/api/workspaces';

  /**
   * Get all workspaces with pagination and filtering
   */
  async getWorkspaces(options?: PaginationOptions, filters?: WorkspaceFilterOptions): Promise<PaginatedResponse<Workspace>> {
    const params = new URLSearchParams();
    
    if (options?.limit) {
      params.append('limit', options.limit.toString());
    }
    if (options?.nextToken) {
      params.append('nextToken', options.nextToken);
    }
    
    if (filters?.tenantId) {
      params.append('tenantId', filters.tenantId);
    }
    if (filters?.accessEvoRegion) {
      params.append('accessEvoRegion', filters.accessEvoRegion);
    }
    if (filters?.applicationSubscriptionId) {
      params.append('applicationSubscriptionId', filters.applicationSubscriptionId);
    }
    if (filters?.organisationId) {
      params.append('organisationId', filters.organisationId);
    }
    if (filters?.salesforceId) {
      params.append('salesforceId', filters.salesforceId);
    }
    
    const url = params.toString() ? `${this.basePath}?${params}` : this.basePath;
    const response = await apiClient.get<ApiResponse<WorkspaceListResponse>>(url);
    
    return {
      items: response.data.items.map(mapWorkspaceFromApi),
      nextToken: response.data.nextToken,
      hasMore: response.data.hasMore
    };
  }

  /**
   * Get a specific workspace by tenant ID
   */
  async getWorkspace(tenantId: string): Promise<Workspace> {
    const response = await apiClient.get<ApiResponse<any>>(`${this.basePath}/${tenantId}`);
    return mapWorkspaceFromApi(response.data);
  }

  /**
   * Create a new workspace
   */
  async createWorkspace(_data: WorkspaceCreateRequest): Promise<Workspace> {
    throw new Error('Not Implemented');
  }

  /**
   * Update an existing workspace
   */
  async updateWorkspace(_tenantId: string, _data: WorkspaceUpdateRequest): Promise<Workspace> {
    throw new Error('Not Implemented');
  }

  /**
   * Delete a workspace
   */
  async deleteWorkspace(tenantId: string): Promise<void> {
    await apiClient.delete<ApiResponse<void>>(`${this.basePath}/${tenantId}`);
  }

  /**
   * Get workspace statistics with optional filtering
   */
  async getWorkspaceStatistics(filters?: WorkspaceFilterOptions): Promise<WorkspaceStatistics> {
    const params = new URLSearchParams();
    
    if (filters?.tenantId) {
      params.append('tenantId', filters.tenantId);
    }
    if (filters?.accessEvoRegion) {
      params.append('accessEvoRegion', filters.accessEvoRegion);
    }
    if (filters?.applicationSubscriptionId) {
      params.append('applicationSubscriptionId', filters.applicationSubscriptionId);
    }
    if (filters?.organisationId) {
      params.append('organisationId', filters.organisationId);
    }
    if (filters?.salesforceId) {
      params.append('salesforceId', filters.salesforceId);
    }
    
    const url = params.toString() ? `${this.basePath}/statistics?${params}` : `${this.basePath}/statistics`;
    const response = await apiClient.get<ApiResponse<WorkspaceStatistics>>(url);
    return response.data;
  }

  /**
   * Recursively fetch workspaces until we have at least the requested limit or no more data.
   * nextToken will be set only if there is more data to fetch for the next page.
   */
  async getWorkspacesAccumulate(
    options?: PaginationOptions,
    filters?: WorkspaceFilterOptions
  ): Promise<PaginatedResponse<Workspace>> {
    const limit = options?.limit || 25;
    let accumulated: Workspace[] = [];
    let nextToken = options?.nextToken;
    let hasMore = true;
    let lastPageNextToken: string | undefined = undefined;

    while (accumulated.length < limit && hasMore) {
      // Always fetch with the full page limit for consistency
      const page = await this.getWorkspaces(
        { limit, nextToken },
        filters
      );
      accumulated = accumulated.concat(page.items);
      lastPageNextToken = page.nextToken;
      hasMore = !!page.nextToken && page.hasMore;
      console.log(`Accumulated ${accumulated.length} workspaces, nextToken: ${nextToken}, hasMore: ${hasMore}`);
      nextToken = page.nextToken;
      if (!hasMore) break;
    }

    // Only set nextToken if we actually have more data to fetch for the next page
    const finalNextToken = hasMore ? lastPageNextToken : undefined;

    return {
      // Return up to the requested limit
      items: accumulated.slice(0, limit),
      nextToken: finalNextToken,
      hasMore: !!finalNextToken
    };
  }
}

export const workspaceService = new WorkspaceService();
